import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanActivateFn, UrlTree } from '@angular/router';
import { ProfileService } from '@fairandsmart/angular';
import { FsCguStatus } from '@fairandsmart/types';
import { environment } from '@Env';
import { CguDialogComponent } from '@Common/cgu-dialog/cgu-dialog.component';
import { AuthService } from '@Services/auth.service';
import { CGU } from '@Utils/cgu';

// Helper function for dialog
function openCguDialog(dialog: MatDialog): Promise<boolean> {
  const dialogRef = dialog.open(CguDialogComponent, {
    width: '650px',
    maxWidth: '90vw',
    autoFocus: false,
    disableClose: true,
    data: {
      readOnly: false,
      cgu: CGU,
    },
  });

  return dialogRef.afterClosed().toPromise()
    .then((result: FsCguStatus) => result === FsCguStatus.UP_TO_DATE);
}

export const cguGuard: CanActivateFn = (): Promise<boolean | UrlTree> | boolean | UrlTree => {
  const auth = inject(AuthService);
  const dialog = inject(MatDialog);
  const profileService = inject(ProfileService);

  if (!auth.isAuthenticated()) {
    return true;
  }

  return auth.ready
    .then(async () => {
      // Auto-accept CGU if customization flag is set
      if (environment.customization.autoAcceptCgu) {
        await profileService.updateCgu(CGU.version).toPromise();
        return true;
      }

      // Check CGU version
      const cguState = profileService.checkCguVersion(CGU);
      if (cguState === FsCguStatus.UP_TO_DATE) {
        return true;
      }

      // Open dialog if outdated
      const cguAccepted = await openCguDialog(dialog);
      if (cguAccepted) {
        return true;
      }

      auth.keycloak.logout();
      return false;
    })
    .catch((error: any) => {
      console.error('An unexpected error occurred while checking CGUs', error);
      return false;
    });
};
